'use client';

// Shared liquid-glass bottom-nav engine — the single source of truth used by
// BOTH the production `MobileNav` and the workspace `BottomTabsSection` preview.
// It is a controlled component: the parent owns the active index (production
// derives it from `usePathname`, the preview from local demo state); this
// component only renders the bar and runs the imperative lift / refraction /
// settle animation. The visual + motion engine is intentionally imperative (a
// 1:1 port of docs/mockups/liquid-glass-nav.html) and sealed inside effects so
// a parent re-render never clobbers the imperatively-managed animation.
import { c as _c } from "react/compiler-runtime";
import './liquid-glass-nav.css';
import type { CSSProperties, RefObject } from 'react';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Icon, type IconSvgElement } from '@/components/ui/icon';
import { NetworkLink } from '@/i18n/network-link';
import { cn } from '@/lib/ui/utils';
import { LiquidGlassNavFilter } from './liquid-glass-nav-filter';
import { DEFAULT_KNOBS, type LiquidGlassNavKnobs, supportsBackdropSvg } from './liquid-glass-nav-knobs';
export type LiquidGlassNavTheme = 'dark' | 'light';
export interface LiquidGlassNavItem {
  /** Stable React key + identity. */
  key: string;
  /** Already-translated label text. */
  label: string;
  icon: IconSvgElement;
  /** When set, the tab renders as a navigating Link; otherwise a plain button. */
  href?: string;
}
interface LiquidGlassNavProps {
  items: readonly LiquidGlassNavItem[];
  /** Active tab index. `-1` (or out of range) parks the bar with no lifted pill. */
  activeIndex: number;
  /** Fired on tab tap, before navigation — lets the parent track selection. */
  onSelect?: (index: number) => void;
  theme: LiquidGlassNavTheme;
  /** Positioning / sizing for the root box (e.g. the production fixed bar). */
  className?: string;
  /** Rendered behind the bar (preview faux content); production leaves it empty
   *  so the real page scrolls behind the glass. */
  backdrop?: React.ReactNode;
  /** Play a one-time announce animation on mount (preview only). */
  autoDemo?: boolean;
  /** Freeze the lens lifted + refracting for static inspection (preview dev). */
  holdLifted?: boolean;
  knobs?: Partial<LiquidGlassNavKnobs>;
  /** Uniform scale of the entire bar (1 = native size). Scales everything
   *  together via a CSS transform — nothing inside changes proportionally. */
  scale?: number;
  /** Accessible label for the nav landmark. Omitted when undefined (parity with
   *  the prior unlabelled bottom `<nav>`). */
  ariaLabel?: string;
}

// Fixed lens box (matches .lgnav-lens / .lgnav-indicator in the CSS).
const LENS_W = 92;
const LENS_H = 58;

// ── Displacement map: convex pill lens (neutral centre, refraction ramps to the
//    rim). Only used while the lens is lifted. ──────────────────────────────────
function buildDisplacementMap(w: number, h: number, exp: number = 2.4): string | null {
  // Use pixel-grid centre (w-1)/2, (h-1)/2 so the SDF is exactly symmetric for
  // the w-1-x / h-1-y mirrors used in the quarter-symmetry loop below.
  const r = (h - 1) / 2;
  const cx = (w - 1) / 2;
  const cy = (h - 1) / 2;
  const cv = document.createElement('canvas');
  cv.width = w;
  cv.height = h;
  const ctx = cv.getContext('2d');
  if (!ctx) return null;
  const img = ctx.createImageData(w, h);
  const d = img.data;
  const EXP = exp;
  const FEATHER = 6;
  const sdf = (px: number, py: number): number => {
    const qx = Math.abs(px - cx) - (cx - r);
    const qy = Math.abs(py - cy) - (cy - r);
    const ax = Math.max(qx, 0);
    const ay = Math.max(qy, 0);
    return Math.hypot(ax, ay) + Math.min(Math.max(qx, qy), 0) - r;
  };
  const shape = (n: number): number => Math.sign(n) * Math.abs(n) ** EXP;
  // Pill has horizontal + vertical symmetry: compute the top-left quadrant and
  // write the result into all four mirror positions. Horizontal mirror inverts
  // the x-displacement (rC), vertical mirror inverts the y-displacement (gC).
  const hw = Math.ceil(w / 2);
  const hh = Math.ceil(h / 2);
  const set = (px: number, py: number, rc: number, gc: number): void => {
    const i = (py * w + px) * 4;
    d[i] = rc;
    d[i + 1] = gc;
    d[i + 2] = 128;
    d[i + 3] = 255;
  };
  for (let y = 0; y < hh; y++) {
    for (let x = 0; x < hw; x++) {
      const nx = x / (w - 1) * 2 - 1;
      const ny = y / (h - 1) * 2 - 1;
      let rC = 128;
      let gC = 128;
      const dist = sdf(x, y);
      if (dist < 0) {
        const edge = Math.min(1, -dist / FEATHER);
        rC = 128 + shape(nx) * 127 * (1 - 0.15 * (1 - edge));
        gC = 128 + shape(ny) * 127 * (1 - 0.15 * (1 - edge));
        rC = 128 + (rC - 128) * (0.4 + 0.6 * edge);
        gC = 128 + (gC - 128) * (0.4 + 0.6 * edge);
      }
      const rCm = 256 - rC;
      const gCm = 256 - gC;
      set(x, y, rC, gC);
      set(w - 1 - x, y, rCm, gC);
      set(x, h - 1 - y, rC, gCm);
      set(w - 1 - x, h - 1 - y, rCm, gCm);
    }
  }
  ctx.putImageData(img, 0, 0);
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const octx = out.getContext('2d');
  if (!octx) return cv.toDataURL();
  octx.filter = 'blur(1.5px)';
  octx.drawImage(cv, 0, 0);
  return out.toDataURL();
}

// Bottom-dock tab link. `prefetch` is the literal `true` (full-RSC) from the
// initial render — Next then full-prefetches each tab's RSC (incl. the
// SSR-dehydrated SDK cache) on viewport-enter. The dock is always on-screen, so
// every tab is warm ahead of the tap → zero loading state on tap. (A
// runtime-toggled prefetch would only do a shell prefetch; it must be literal.)
function LiquidGlassNavTabLink(t0) {
  const $ = _c(6);
  const {
    href,
    active,
    onClick,
    children
  } = t0;
  const t1 = active ? "true" : "false";
  const t2 = active ? "page" : undefined;
  let t3;
  if ($[0] !== children || $[1] !== href || $[2] !== onClick || $[3] !== t1 || $[4] !== t2) {
    t3 = <NetworkLink href={href} prefetch={true} className="lgnav-tab" data-active={t1} aria-current={t2} onClick={onClick}>{children}</NetworkLink>;
    $[0] = children;
    $[1] = href;
    $[2] = onClick;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
interface LiquidGlassNavEngine {
  rootRef: RefObject<HTMLDivElement | null>;
  refraction: boolean;
  mapUrl: string | null;
  canFrost: boolean;
  handleClick: (i: number) => void;
}

// The whole imperative engine — capability probes, displacement map, lift /
// refraction / settle animation, and the controlled-index driver — lives in
// this hook so the component body stays a thin renderer. (Extracting it to a
// hook, not a child, is deliberate: the sealed effects do imperative DOM writes
// against the bar's own descendants via `rootRef`, and a parent re-render must
// never clobber that animation.)
function useLiquidGlassNavEngine(t0) {
  const $ = _c(42);
  const {
    items,
    activeIndex,
    autoDemo,
    holdLifted,
    onSelect,
    k,
    dispRId,
    dispGId,
    dispBId
  } = t0;
  const [refraction, setRefraction] = useState(false);
  const [mapUrl, setMapUrl] = useState(null);
  const [canFrost, setCanFrost] = useState(false);
  const rootRef = useRef(null);
  const t1 = k.displacement + k.chromatic;
  const t2 = k.displacement - k.chromatic;
  let t3;
  if ($[0] !== k.displacement || $[1] !== t1 || $[2] !== t2) {
    t3 = {
      r: t1,
      g: k.displacement,
      b: t2
    };
    $[0] = k.displacement;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const peak = t3;
  const peakRef = useRef(peak);
  const bulgeRef = useRef(k.bulge);
  const activeIndexRef = useRef(activeIndex);
  let t4;
  let t5;
  if ($[4] !== activeIndex || $[5] !== k.bulge || $[6] !== peak.b || $[7] !== peak.g || $[8] !== peak.r) {
    t4 = () => {
      peakRef.current = {
        r: peak.r,
        g: peak.g,
        b: peak.b
      };
      bulgeRef.current = k.bulge;
      activeIndexRef.current = activeIndex;
    };
    t5 = [activeIndex, k.bulge, peak.r, peak.g, peak.b];
    $[4] = activeIndex;
    $[5] = k.bulge;
    $[6] = peak.b;
    $[7] = peak.g;
    $[8] = peak.r;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useLayoutEffect(t4, t5);
  const selectRef = useRef(_temp);
  let t6;
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const blurOk = CSS.supports("backdrop-filter", "blur(1px)") || CSS.supports("-webkit-backdrop-filter", "blur(1px)");
      const reduced = matchMedia("(prefers-reduced-transparency: reduce)").matches;
      setCanFrost(blurOk && !reduced);
    };
    t7 = [];
    $[11] = t6;
    $[12] = t7;
  } else {
    t6 = $[11];
    t7 = $[12];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[13] !== k.edge) {
    t8 = () => {
      const url = buildDisplacementMap(LENS_W, LENS_H, k.edge);
      if (url) {
        setMapUrl(url);
      }
    };
    t9 = [k.edge];
    $[13] = k.edge;
    $[14] = t8;
    $[15] = t9;
  } else {
    t8 = $[14];
    t9 = $[15];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[16] !== dispBId || $[17] !== dispGId || $[18] !== dispRId || $[19] !== holdLifted || $[20] !== peak.b || $[21] !== peak.g || $[22] !== peak.r) {
    t10 = () => {
      const s = holdLifted ? {
        r: peak.r,
        g: peak.g,
        b: peak.b
      } : {
        r: 0,
        g: 0,
        b: 0
      };
      document.getElementById(dispRId)?.setAttribute("scale", String(s.r));
      document.getElementById(dispGId)?.setAttribute("scale", String(s.g));
      document.getElementById(dispBId)?.setAttribute("scale", String(s.b));
      if (!holdLifted) {
        return;
      }
      const root = rootRef.current;
      const lens = root?.querySelector(".lgnav-lens");
      const tabs = root ? Array.from(root.querySelectorAll(".lgnav-tab")) : [];
      const boundaryTab = tabs[Math.min(2, tabs.length - 1)];
      if (lens && boundaryTab) {
        lens.style.transition = "none";
        lens.style.translate = `${boundaryTab.offsetLeft - lens.offsetWidth / 2 - lens.offsetLeft}px 0`;
      }
    };
    t11 = [holdLifted, peak.r, peak.g, peak.b, dispRId, dispGId, dispBId];
    $[16] = dispBId;
    $[17] = dispGId;
    $[18] = dispRId;
    $[19] = holdLifted;
    $[20] = peak.b;
    $[21] = peak.g;
    $[22] = peak.r;
    $[23] = t10;
    $[24] = t11;
  } else {
    t10 = $[23];
    t11 = $[24];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[25] !== autoDemo || $[26] !== dispBId || $[27] !== dispGId || $[28] !== dispRId || $[29] !== items.length) {
    t12 = () => {
      const root_0 = rootRef.current;
      if (!root_0) {
        return;
      }
      const indicator = root_0.querySelector(".lgnav-indicator");
      const lens_0 = root_0.querySelector(".lgnav-lens");
      const dispR = document.getElementById(dispRId);
      const dispG = document.getElementById(dispGId);
      const dispB = document.getElementById(dispBId);
      const tabs_0 = Array.from(root_0.querySelectorAll(".lgnav-tab"));
      if (tabs_0.length !== items.length) {
        return;
      }
      if (!indicator || !lens_0 || !dispR || !dispG || !dispB) {
        return;
      }
      const reduceMotion = matchMedia("(prefers-reduced-motion: reduce)").matches;
      const refractionMode = supportsBackdropSvg() ? "full" : "blur";
      if (refractionMode === "full") {
        setRefraction(true);
      }
      let current = -1;
      const targetX = i => {
        const tab = tabs_0[i];
        if (!tab) {
          return 0;
        }
        return tab.offsetLeft + tab.offsetWidth / 2 - indicator.offsetWidth / 2 - indicator.offsetLeft;
      };
      const setScale = s_0 => {
        if (refractionMode !== "full") {
          return;
        }
        dispR.setAttribute("scale", String(s_0.r));
        dispG.setAttribute("scale", String(s_0.g));
        dispB.setAttribute("scale", String(s_0.b));
      };
      let rampId = 0;
      let rampFrame = 0;
      const rampRefraction = duration => {
        if (refractionMode !== "full" || reduceMotion) {
          return;
        }
        rampId = rampId + 1;
        const id = rampId;
        const start = performance.now();
        const step = now => {
          if (id !== rampId) {
            return;
          }
          const t = Math.min(1, (now - start) / duration);
          const env = Math.sin(Math.PI * t);
          const p = peakRef.current;
          setScale({
            r: p.r * env,
            g: p.g * env,
            b: p.b * env
          });
          if (t < 1) {
            rampFrame = requestAnimationFrame(step);
          } else {
            setScale({
              r: 0,
              g: 0,
              b: 0
            });
          }
        };
        rampFrame = requestAnimationFrame(step);
      };
      let pillTimer = 0;
      let pillAnim = null;
      const placePill = (x, fade) => {
        indicator.style.transition = "none";
        indicator.style.translate = `${x}px 0`;
        if (!fade) {
          indicator.style.opacity = "1";
          return;
        }
        indicator.style.transition = "opacity 0.18s var(--ease-default)";
        indicator.style.opacity = "1";
        if (reduceMotion) {
          return;
        }
        if (pillAnim) {
          pillAnim.cancel();
        }
        pillAnim = indicator.animate([{
          scale: "0.86 0.9"
        }, {
          scale: "1.07 1.05",
          offset: 0.24
        }, {
          scale: "0.97 0.99",
          offset: 0.44
        }, {
          scale: "1.035 1.015",
          offset: 0.62
        }, {
          scale: "0.99 1.0",
          offset: 0.78
        }, {
          scale: "1.012 1.0",
          offset: 0.9
        }, {
          scale: "1 1"
        }], {
          duration: 560,
          easing: "linear",
          fill: "none"
        });
      };
      let lensAnim = null;
      const liftLens = (startX, endX) => {
        if (reduceMotion) {
          return;
        }
        lens_0.style.transition = "none";
        lens_0.style.translate = `${startX}px 0`;
        lens_0.style.transition = "translate 400ms var(--ease-spring)";
        lens_0.style.translate = `${endX}px 0`;
        rampRefraction(400);
        if (lensAnim) {
          lensAnim.cancel();
        }
        const B = bulgeRef.current;
        lensAnim = lens_0.animate([{
          opacity: 0,
          scale: "1 1"
        }, {
          opacity: 1,
          scale: `1.06 ${B}`,
          offset: 0.26
        }, {
          opacity: 1,
          scale: `1.03 ${1 + (B - 1) * 0.5}`,
          offset: 0.46
        }, {
          opacity: 1,
          scale: "0.99 0.94",
          offset: 0.63
        }, {
          opacity: 1,
          scale: "1.0 1.09",
          offset: 0.79
        }, {
          opacity: 0.45,
          scale: "1 1.0",
          offset: 0.92
        }, {
          opacity: 0,
          scale: "1 1"
        }], {
          duration: 400,
          easing: "linear",
          fill: "none"
        });
        lensAnim.onfinish = () => setScale({
          r: 0,
          g: 0,
          b: 0
        });
      };
      const select = (i_0, animate) => {
        const prev = current;
        current = i_0;
        tabs_0.forEach((t_0, kIdx) => {
          t_0.setAttribute("data-active", kIdx === i_0 ? "true" : "false");
        });
        window.clearTimeout(pillTimer);
        if (i_0 < 0 || i_0 >= tabs_0.length) {
          indicator.style.transition = "none";
          indicator.style.opacity = "0";
          return;
        }
        const endX_0 = targetX(i_0);
        if (!animate || prev === i_0 || prev < 0 || reduceMotion) {
          placePill(endX_0, false);
          return;
        }
        indicator.style.transition = "opacity 0.16s var(--ease-default)";
        indicator.style.opacity = "0";
        liftLens(targetX(prev), endX_0);
        pillTimer = window.setTimeout(() => placePill(endX_0, true), 260);
      };
      selectRef.current = select;
      setScale({
        r: 0,
        g: 0,
        b: 0
      });
      select(activeIndexRef.current, false);
      let rAF = 0;
      const observer = new ResizeObserver(() => {
        cancelAnimationFrame(rAF);
        rAF = requestAnimationFrame(() => {
          if (current < 0) {
            return;
          }
          indicator.style.transition = "none";
          indicator.style.translate = `${targetX(current)}px 0`;
        });
      });
      observer.observe(root_0);
      let demo1 = 0;
      let demo2 = 0;
      if (autoDemo && tabs_0.length > 2) {
        demo1 = window.setTimeout(() => select(2, true), 800);
        demo2 = window.setTimeout(() => select(0, true), 1700);
      }
      return () => {
        selectRef.current = _temp2;
        observer.disconnect();
        cancelAnimationFrame(rAF);
        cancelAnimationFrame(rampFrame);
        window.clearTimeout(pillTimer);
        window.clearTimeout(demo1);
        window.clearTimeout(demo2);
        if (lensAnim) {
          lensAnim.cancel();
        }
        if (pillAnim) {
          pillAnim.cancel();
        }
      };
    };
    t13 = [items.length, autoDemo, dispRId, dispGId, dispBId];
    $[25] = autoDemo;
    $[26] = dispBId;
    $[27] = dispGId;
    $[28] = dispRId;
    $[29] = items.length;
    $[30] = t12;
    $[31] = t13;
  } else {
    t12 = $[30];
    t13 = $[31];
  }
  useEffect(t12, t13);
  const firstWatch = useRef(true);
  let t14;
  let t15;
  if ($[32] !== activeIndex) {
    t14 = () => {
      if (firstWatch.current) {
        firstWatch.current = false;
        return;
      }
      selectRef.current(activeIndex, true);
    };
    t15 = [activeIndex];
    $[32] = activeIndex;
    $[33] = t14;
    $[34] = t15;
  } else {
    t14 = $[33];
    t15 = $[34];
  }
  useEffect(t14, t15);
  let t16;
  if ($[35] !== onSelect) {
    t16 = i_1 => {
      onSelect?.(i_1);
      requestAnimationFrame(() => {
        selectRef.current(i_1, true);
      });
    };
    $[35] = onSelect;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  const handleClick = t16;
  let t17;
  if ($[37] !== canFrost || $[38] !== handleClick || $[39] !== mapUrl || $[40] !== refraction) {
    t17 = {
      rootRef,
      refraction,
      mapUrl,
      canFrost,
      handleClick
    };
    $[37] = canFrost;
    $[38] = handleClick;
    $[39] = mapUrl;
    $[40] = refraction;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  return t17;
}
function _temp2() {}
function _temp() {}
export function LiquidGlassNav(t0) {
  const $ = _c(74);
  const {
    items,
    activeIndex,
    onSelect,
    theme,
    className,
    backdrop,
    autoDemo: t1,
    holdLifted: t2,
    knobs,
    scale: t3,
    ariaLabel
  } = t0;
  const autoDemo = t1 === undefined ? false : t1;
  const holdLifted = t2 === undefined ? false : t2;
  const scale = t3 === undefined ? 1 : t3;
  let t4;
  if ($[0] !== knobs) {
    t4 = {
      ...DEFAULT_KNOBS,
      ...knobs
    };
    $[0] = knobs;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const k = t4;
  const t5 = useId();
  let t6;
  if ($[2] !== t5) {
    let t7;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = /:/g;
      $[4] = t7;
    } else {
      t7 = $[4];
    }
    t6 = t5.replace(t7, "");
    $[2] = t5;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const uid = t6;
  const filterId = `lgnav-filter-${uid}`;
  const dispRId = `${filterId}-r`;
  const dispGId = `${filterId}-g`;
  const dispBId = `${filterId}-b`;
  let t7;
  if ($[5] !== activeIndex || $[6] !== autoDemo || $[7] !== dispBId || $[8] !== dispGId || $[9] !== dispRId || $[10] !== holdLifted || $[11] !== items || $[12] !== k || $[13] !== onSelect) {
    t7 = {
      items,
      activeIndex,
      autoDemo,
      holdLifted,
      onSelect,
      k,
      dispRId,
      dispGId,
      dispBId
    };
    $[5] = activeIndex;
    $[6] = autoDemo;
    $[7] = dispBId;
    $[8] = dispGId;
    $[9] = dispRId;
    $[10] = holdLifted;
    $[11] = items;
    $[12] = k;
    $[13] = onSelect;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const {
    rootRef,
    refraction,
    mapUrl,
    canFrost,
    handleClick
  } = useLiquidGlassNavEngine(t7);
  let t8;
  if ($[15] !== filterId || $[16] !== k.frost || $[17] !== refraction) {
    t8 = refraction ? {
      backdropFilter: `blur(${k.frost}px) url(#${filterId})`,
      WebkitBackdropFilter: `blur(${k.frost}px) url(#${filterId})`
    } : undefined;
    $[15] = filterId;
    $[16] = k.frost;
    $[17] = refraction;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const warpStyle = t8;
  let t9;
  if ($[19] !== canFrost) {
    t9 = canFrost ? {
      backdropFilter: "blur(8px) saturate(160%)",
      WebkitBackdropFilter: "blur(8px) saturate(160%)"
    } : undefined;
    $[19] = canFrost;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const frostStyle = t9;
  let t10;
  if ($[21] !== dispBId || $[22] !== dispGId || $[23] !== dispRId || $[24] !== filterId || $[25] !== mapUrl) {
    t10 = <LiquidGlassNavFilter filterId={filterId} dispRId={dispRId} dispGId={dispGId} dispBId={dispBId} mapUrl={mapUrl} lensW={LENS_W} lensH={LENS_H} />;
    $[21] = dispBId;
    $[22] = dispGId;
    $[23] = dispRId;
    $[24] = filterId;
    $[25] = mapUrl;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const t11 = refraction && "has-refraction";
  const t12 = holdLifted && "demo-lifted";
  const t13 = canFrost && "lgnav-can-frost";
  let t14;
  if ($[27] !== className || $[28] !== t11 || $[29] !== t12 || $[30] !== t13) {
    t14 = cn("lgnav-root", t11, t12, t13, className);
    $[27] = className;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const t15 = String(k.tint);
  const t16 = String(k.bulge);
  const t17 = `${k.ringThickness}px`;
  const t18 = `${k.ringBlur}px`;
  const t19 = `${k.specularAngle}deg`;
  const t20 = String(k.glow);
  let t21;
  if ($[32] !== t15 || $[33] !== t16 || $[34] !== t17 || $[35] !== t18 || $[36] !== t19 || $[37] !== t20) {
    t21 = {
      "--lens-tint-alpha": t15,
      "--bulge": t16,
      "--ring-thickness": t17,
      "--ring-blur": t18,
      "--specular-angle": t19,
      "--lens-glow": t20
    };
    $[32] = t15;
    $[33] = t16;
    $[34] = t17;
    $[35] = t18;
    $[36] = t19;
    $[37] = t20;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  const t22 = t21 as CSSProperties;
  let t23;
  if ($[39] !== scale) {
    t23 = scale === 1 ? undefined : {
      zoom: scale
    };
    $[39] = scale;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== frostStyle) {
    t24 = <span className="lgnav-blur" aria-hidden="true" style={frostStyle} />;
    $[41] = frostStyle;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <span className="lgnav-indicator" aria-hidden="true" />;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  let t26;
  if ($[44] !== warpStyle) {
    t26 = <span className="lgnav-lens-warp" style={warpStyle} />;
    $[44] = warpStyle;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  let t27;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <span className="lgnav-lens-ring" aria-hidden="true" />;
    $[46] = t27;
  } else {
    t27 = $[46];
  }
  let t28;
  if ($[47] !== t26) {
    t28 = <span className="lgnav-lens" aria-hidden="true">{t26}{t27}</span>;
    $[47] = t26;
    $[48] = t28;
  } else {
    t28 = $[48];
  }
  let t29;
  if ($[49] !== activeIndex || $[50] !== handleClick || $[51] !== items) {
    let t30;
    if ($[53] !== activeIndex || $[54] !== handleClick) {
      t30 = (item, i) => {
        const active = i === activeIndex;
        const content = <><Icon icon={item.icon} /><span>{item.label}</span></>;
        if (item.href !== undefined) {
          return <LiquidGlassNavTabLink key={item.key} href={item.href} active={active} onClick={() => handleClick(i)}>{content}</LiquidGlassNavTabLink>;
        }
        return <button key={item.key} type="button" className="lgnav-tab" data-active={active ? "true" : "false"} aria-pressed={active} onClick={() => handleClick(i)}>{content}</button>;
      };
      $[53] = activeIndex;
      $[54] = handleClick;
      $[55] = t30;
    } else {
      t30 = $[55];
    }
    t29 = items.map(t30);
    $[49] = activeIndex;
    $[50] = handleClick;
    $[51] = items;
    $[52] = t29;
  } else {
    t29 = $[52];
  }
  let t30;
  if ($[56] !== t24 || $[57] !== t28 || $[58] !== t29) {
    t30 = <div className="lgnav-track">{t24}{t25}{t28}{t29}</div>;
    $[56] = t24;
    $[57] = t28;
    $[58] = t29;
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  let t31;
  if ($[60] !== ariaLabel || $[61] !== t23 || $[62] !== t30) {
    t31 = <nav className="lgnav-wrap" aria-label={ariaLabel} style={t23}>{t30}</nav>;
    $[60] = ariaLabel;
    $[61] = t23;
    $[62] = t30;
    $[63] = t31;
  } else {
    t31 = $[63];
  }
  let t32;
  if ($[64] !== backdrop || $[65] !== rootRef || $[66] !== t14 || $[67] !== t22 || $[68] !== t31 || $[69] !== theme) {
    t32 = <div ref={rootRef} data-theme={theme} className={t14} style={t22}>{backdrop}{t31}</div>;
    $[64] = backdrop;
    $[65] = rootRef;
    $[66] = t14;
    $[67] = t22;
    $[68] = t31;
    $[69] = theme;
    $[70] = t32;
  } else {
    t32 = $[70];
  }
  let t33;
  if ($[71] !== t10 || $[72] !== t32) {
    t33 = <>{t10}{t32}</>;
    $[71] = t10;
    $[72] = t32;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  return t33;
}